import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import ErrorPage403 from '@/app/routes/errorPage403';
import Loader from '@/app/routes/Loader';
import { Button } from '@/shared/components/Buttons';
import { ButtonVariant } from '@/shared/components/Buttons/types';
import InlineLayout from '@/shared/layout/inlineLayout';
import { SERVICE_TYPE_LABELS } from '../../constants/labels';
import {
  SERVICE_TYPE_NAV_ITEMS,
  useViewServiceType,
} from '../../hooks/useViewServiceType';
import ViewServiceTypeContent from './ViewServiceTypeContent';
import ViewServiceTypeErrorScreen from './ViewServiceTypeErrorScreen';

const EDIT_PATH_PREFIX = '/Typy-usług/Edycja-typu-usługi';

const ViewServiceType: React.FC<{ serviceTypeId: string }> = ({
  serviceTypeId,
}) => {
  const navigate = useNavigate();
  const {
    serviceType,
    isLoading,
    showErrorScreen,
    notFound,
    accessError,
    statusLabel,
    statusBadgeClasses,
    formattedDescription,
    formattedCreatedAt,
    formattedUpdatedAt,
    formattedUpdatedBy,
  } = useViewServiceType(serviceTypeId);

  const serviceHeader = (
    <div className="flex items-center justify-between w-full gap-4">
      <div className="flex min-w-0 flex-1">
        <div className="flex min-w-0 flex-col gap-[4px]">
          <div className="flex min-w-0 flex-col items-start gap-1">
            <h2
              className="min-w-0 break-all font-medium text-neutral-10"
              style={{ fontSize: '18px', lineHeight: '28px' }}
              data-testid="service-type-name"
            >
              {serviceType?.service_name ?? '—'}
            </h2>
            <span
              className={`px-3 py-1 rounded-[8px] text-sm font-medium flex items-center gap-2 ${statusBadgeClasses.container}`}
              style={{ fontSize: '14px', lineHeight: '20px' }}
            >
              <div
                className={`rounded-full shrink-0 ${statusBadgeClasses.dot}`}
                style={{ width: '6px', height: '6px' }}
              />
              {statusLabel}
            </span>
          </div>
        </div>
      </div>
      <Button
        label={SERVICE_TYPE_LABELS.BUTTON_EDIT}
        variant={ButtonVariant.Primary}
        size="Medium"
        onClick={() => navigate(`${EDIT_PATH_PREFIX}/${serviceTypeId}`)}
        className="!py-[10px] !px-[16px] !font-medium !text-[14px] hover:cursor-pointer shrink-0"
        style={{ borderRadius: '8px', height: '40px' }}
      />
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  return (
    <React.Fragment key={serviceTypeId}>
      {showErrorScreen ? (
        <ViewServiceTypeErrorScreen />
      ) : (
        <InlineLayout
          title={SERVICE_TYPE_LABELS.VIEW_TITLE}
          navItems={[...SERVICE_TYPE_NAV_ITEMS]}
          contentByNavId={{
            PodstawoweInformacje: (
              <ViewServiceTypeContent
                formattedDescription={formattedDescription}
                formattedCreatedAt={formattedCreatedAt}
                formattedUpdatedAt={formattedUpdatedAt}
                formattedUpdatedBy={formattedUpdatedBy}
              />
            ),
          }}
          header={serviceHeader}
          searchParamKey="tab"
          defaultActiveId="PodstawoweInformacje"
        />
      )}
    </React.Fragment>
  );
};

export default ViewServiceType;
